/** Admin -> Branches (eskiz 23): filiallar va vaqt zonalari (jamoalar olib tashlangan, 2026-10-18). */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ApiError } from "@/lib/api";
import { cn, dayjs } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import type { Me } from "@/features/auth/types";
import { useBranches, useCreateBranch, useUpdateBranch } from "@/features/admin/api";
import { TIMEZONES, type Branch, type BranchWrite } from "@/features/admin/types";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { EmptyState, ErrorState, Kv, PageLoading } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { ContextMenu } from "@/components/ui/context-menu";
import { useBranchFilter } from "@/app/branchFilter";
import { branchMenu } from "@/features/admin/menu";
import { PersonLinks } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";

export function BranchesPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Branches me={me.data} />;
}

function Branches({ me }: { me: Me }) {
  const { t } = useTranslation("admin");
  const isOwner = me.role === "owner";
  const branches = useBranches();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [branchModal, setBranchModal] = useState<{ open: boolean; branch?: Branch | null }>({ open: false });
  const navigate = useNavigate();
  const { t: tmenu } = useTranslation("menu");
  const { setBranchId } = useBranchFilter();
  const personNav = usePersonNav();
  const branchItems = (b: Branch) =>
    branchMenu(
      b,
      me,
      {
        navigate: (path) => navigate(path),
        openDashboard: () => {
          setBranchId(b.id);
          navigate("/");
        },
        openBudget: () => {
          setBranchId(b.id);
          navigate("/budget");
        },
        edit: () => setBranchModal({ open: true, branch: b }),
        profile: personNav,
      },
      tmenu,
    );
  const list = branches.data?.results ?? [];
  const selected = list.find((b) => b.id === selectedId) ?? list[0] ?? null;
  const totalRecruiters = list.reduce((n, b) => n + b.recruiters_count, 0);

  return (
    <AdminLayout
      title={t("branches.title")}
      subtitle={branches.data ? t("branches.subtitle", { count: list.length, recruiters: totalRecruiters }) : "…"}
      actions={
        <>
          {isOwner && (
            <Button variant="primary" onClick={() => setBranchModal({ open: true, branch: null })}>
              {t("branches.newBranch")}
            </Button>
          )}
        </>
      }
    >
      {branches.isPending ? (
        <PageLoading />
      ) : branches.isError ? (
        <ErrorState message={branches.error.message} onRetry={() => branches.refetch()} />
      ) : list.length === 0 ? (
        <EmptyState title={t("branches.emptyTitle")} description={t("branches.emptyDesc")} />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_2fr]">
          <div className="space-y-4">
            <Card>
              <CardHeader title={t("branches.card")} />
              <CardBody>
                <Table variant="bare">
                  <thead>
                    <tr>
                      <Th>{t("branches.col.branch")}</Th>
                      <Th>{t("branches.col.manager")}</Th>
                      <Th>{t("branches.col.timezone")}</Th>
                      <Th className="text-right">{t("branches.col.recruiters")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((b) => (
                      <ContextMenu key={b.id} items={() => branchItems(b)}>
                        <Tr clickable onClick={() => setSelectedId(b.id)} className={cn(selected?.id === b.id && "bg-primary-soft/60 hover:bg-primary-soft/60")}>
                          <Td className="font-semibold">{b.name}</Td>
                          <Td>
                            <PersonLinks people={b.managers} role="branch_manager" />
                          </Td>
                          <Td>
                            {b.timezone} <span className="text-muted">(UTC{dayjs().tz(b.timezone).format("Z").replace(":00", "")})</span>
                          </Td>
                          <Td className="text-right">{b.recruiters_count}</Td>
                        </Tr>
                      </ContextMenu>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title={t("branches.tz.title")} />
              <CardBody>
                <Kv label={t("branches.tz.branchTime")}>{t("branches.tz.branchTimeDesc")}</Kv>
                <Kv label={t("branches.tz.driverTime")}>{t("branches.tz.driverTimeDesc")}</Kv>
                <Kv label={t("branches.tz.reminders")}>{t("branches.tz.remindersDesc")}</Kv>
                <Kv label={t("branches.tz.today")}>{t("branches.tz.todayDesc")}</Kv>
                <Clocks />
              </CardBody>
            </Card>
          </div>

          {selected && (
            <div className="space-y-4">
              <Card>
                <CardHeader
                  title={selected.name}
                  action={
                    isOwner && (
                      <Button size="sm" variant="link" onClick={() => setBranchModal({ open: true, branch: selected })}>
                        {t("common:actions.edit")}
                      </Button>
                    )
                  }
                />
                <CardBody>
                  <Kv label={t("branches.kv.manager")}>
                    <PersonLinks people={selected.managers} role="branch_manager" />
                  </Kv>
                  <Kv label={t("branches.kv.timezone")}>{selected.timezone}</Kv>
                  <Kv label={t("branches.kv.hours")}>{selected.working_hours || "—"}</Kv>
                  <Kv label={t("branches.kv.address")}>{selected.address || "—"}</Kv>
                  <Kv label={t("branches.kv.recruiters")}>{selected.recruiters_count}</Kv>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      )}

      <BranchFormModal open={branchModal.open} onOpenChange={(o) => setBranchModal((m) => ({ ...m, open: o }))} branch={branchModal.branch} />
    </AdminLayout>
  );
}

/** Jonli soatlar: Toshkent va AQSh zonalari */
function Clocks() {
  const { t } = useTranslation("admin");
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    const id = window.setInterval(() => setNow(dayjs()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const zones: [string, string][] = [
    [t("branches.tz.tashkent"), "Asia/Tashkent"],
    [t("branches.tz.chicago"), "America/Chicago"],
    [t("branches.tz.newYork"), "America/New_York"],
    [t("branches.tz.losAngeles"), "America/Los_Angeles"],
  ];
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {zones.map(([label, tz]) => (
        <span key={tz} className="rounded-full bg-fill px-3 py-1 text-[12px]">
          <span className="font-semibold">{label}</span> {now.tz(tz).format("HH:mm")}
        </span>
      ))}
    </div>
  );
}

// ---- Filial oynasi (owner) ----

interface BranchForm {
  name: string;
  code: string;
  timezone: string;
  address: string;
  working_hours: string;
}

const EMPTY_BRANCH: BranchForm = { name: "", code: "", timezone: "Asia/Tashkent", address: "", working_hours: "" };

function BranchFormModal({ open, onOpenChange, branch }: { open: boolean; onOpenChange: (o: boolean) => void; branch?: Branch | null }) {
  const { t } = useTranslation("admin");
  const editing = !!branch;
  const [form, setForm] = useState<BranchForm>(EMPTY_BRANCH);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const create = useCreateBranch();
  const update = useUpdateBranch();
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      setForm(branch ? { name: branch.name, code: branch.code, timezone: branch.timezone, address: branch.address, working_hours: branch.working_hours } : EMPTY_BRANCH);
      setErrors({});
    }
  }, [open, branch]);

  const set = <K extends keyof BranchForm>(key: K, value: BranchForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const local: Record<string, string[]> = {};
    if (!form.name.trim()) local.name = [t("branches.nameRequired")];
    if (!form.code.trim()) local.code = [t("branches.codeRequired")];
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    const payload: BranchWrite = { name: form.name.trim(), code: form.code.trim().toUpperCase(), timezone: form.timezone, address: form.address.trim(), working_hours: form.working_hours.trim() };
    try {
      if (editing && branch) await update.mutateAsync({ id: branch.id, version: branch.version, ...payload });
      else await create.mutateAsync(payload);
      toast.success(editing ? t("branches.saved") : t("branches.created"));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict) {
          toast.error(err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon"));
          onOpenChange(false);
          return;
        }
        if (err.fields) setErrors(err.fields);
        else toast.error(err.message);
      } else toast.error(t("branches.saveFailed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t("branches.editTitle", { name: branch?.name }) : t("branches.newTitle")}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={(e) => submit(e as unknown as React.FormEvent)}>
            {editing ? t("common:actions.saveChanges") : t("branches.create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <Field label={t("branches.name")} required error={errors.name}>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus placeholder={t("branches.namePlaceholder")} aria-invalid={!!errors.name} />
          </Field>
          <Field label={t("branches.code")} required error={errors.code}>
            <Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder={t("branches.codePlaceholder")} maxLength={10} aria-invalid={!!errors.code} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("branches.kv.timezone")} error={errors.timezone}>
            <Select value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
              {TIMEZONES.map((z) => (
                <option key={z} value={z}>
                  {z} (UTC{dayjs().tz(z).format("Z").replace(":00", "")})
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("branches.kv.hours")} error={errors.working_hours} hint={t("branches.hoursHint")}>
            <Input value={form.working_hours} onChange={(e) => set("working_hours", e.target.value)} />
          </Field>
        </div>
        <Field label={t("branches.kv.address")} error={errors.address}>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
        </Field>
        <p className="text-[11px] text-muted">{t("branches.managerHint")}</p>
      </form>
    </Modal>
  );
}
